/**
 * Service to interact with OpenRouter's API (formerly Anthropic)
 */
class AnthropicService {
  constructor() {
    this.apiKey = process.env.REACT_APP_OPENROUTER_API_KEY;
    this.apiEndpoint = 'https://openrouter.ai/api/v1/chat/completions';
    this.model = 'deepseek/deepseek-chat-v3-0324:free';
  }

  /**
   * Generate a solution for a NEET question using OpenRouter API
   * @param {Object} question - Question object
   * @returns {Promise<Object>} - Generated solution object with solution text and correct answer
   */
  async generateSolution(question) {
    if (!this.apiKey) {
      throw new Error('OpenRouter API key not set. Please set REACT_APP_OPENROUTER_API_KEY in .env file.');
    }

    try {
      const prompt = `
You are an expert NEET exam tutor. Generate a detailed step-by-step solution for the following NEET ${question.subject} question.

Question: ${question.text}

Options:
A: ${question.options.A}
B: ${question.options.B}
C: ${question.options.C}
D: ${question.options.D}

Provide your response in the following format:

CORRECT_ANSWER: [LETTER]
(Just a single letter A, B, C, or D as the correct answer)

SOLUTION:
[Your step-by-step explanation here]
`;

      const response = await fetch(this.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`
        },
        body: JSON.stringify({
          model: this.model,
          messages: [{
            role: 'user',
            content: prompt
          }]
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`OpenRouter API Error: ${errorData.error?.message || response.statusText || 'Unknown error'}`);
      }

      const data = await response.json();
      
      // Extract response text from OpenRouter's response structure
      let responseText = '';
      
      if (data.choices && data.choices.length > 0 && data.choices[0].message) {
        responseText = data.choices[0].message.content;
      } else {
        console.log('OpenRouter response structure:', JSON.stringify(data, null, 2));
        throw new Error('Unable to parse OpenRouter response. See console for details.');
      }
      
      // Extract correct answer from the response
      const answerMatch = responseText.match(/CORRECT_ANSWER:\s*([ABCD])/i);
      const correctAnswer = answerMatch ? answerMatch[1].toUpperCase() : null;
      
      // Extract solution text (everything after "SOLUTION:")
      const solutionMatch = responseText.match(/SOLUTION:([\s\S]*)/i);
      const solutionText = solutionMatch ? solutionMatch[1].trim() : responseText;
      
      if (!correctAnswer) {
        throw new Error('Could not extract correct answer from OpenRouter response');
      }
      
      return {
        solution: solutionText,
        correctAnswer: correctAnswer
      };
    } catch (error) {
      console.error("Error calling OpenRouter API:", error);
      throw error;
    }
  }
  
  /**
   * Generate solutions for multiple questions in batch
   * @param {Array} questions - Array of question objects
   * @returns {Promise<Array>} - Array of solution objects
   */
  async generateSolutions(questions) {
    const solutions = [];
    
    // Process questions sequentially to avoid rate limiting
    for (const question of questions) {
      try {
        const solutionData = await this.generateSolution(question);
        solutions.push({
          questionId: question.id,
          solution: solutionData.solution,
          correctAnswer: solutionData.correctAnswer
        });
      } catch (error) {
        console.error(`Error generating OpenRouter solution for question ${question.id}:`, error);
        // Add a placeholder for failed solutions
        solutions.push({
          questionId: question.id,
          solution: "Error generating solution with OpenRouter. Please try again.",
          correctAnswer: null
        });
      }
      
      // Add a small delay between requests to avoid rate limiting
      await new Promise(resolve => setTimeout(resolve, 500));
    }
    
    return solutions;
  }
}

// Export a singleton instance
export const anthropicService = new AnthropicService();